// Voice Transcribe Button Component
import React, { useState, useRef, useEffect } from 'react';
import { Button, message, Tooltip } from 'antd';
import { LoadingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import VoiceTranscriptionService, { TranscriptionResult, TranscriptionOptions, getVoiceTranscriptionService } from '../services/voiceTranscriptionService';
import './VoiceTranscribe.css';

interface VoiceTranscribeButtonProps {
  onTranscriptionText: (text: string, isPartial?: boolean, replacePartial?: boolean) => void;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  className?: string;
}

type TranscriptionState = 'idle' | 'connecting' | 'recording' | 'processing' | 'error';

const VoiceTranscribeButton: React.FC<VoiceTranscribeButtonProps> = ({
  onTranscriptionText,
  disabled = false,
  size = 'middle',
  className = '',
}) => {
  const [state, setState] = useState<TranscriptionState>('idle');
  const [isSupported, setIsSupported] = useState(true);
  const transcriptionService = useRef<VoiceTranscriptionService | null>(null);
  const processedTexts = useRef<Set<string>>(new Set());
  const lastInsertedText = useRef<string>('');
  const currentPartialText = useRef<string>('');
  const isProcessingPartial = useRef<boolean>(false);

  useEffect(() => {
    // Check browser support on mount
    if (!VoiceTranscriptionService.isSupported()) {
      setIsSupported(false);
      message.error('Voice transcription is not supported in this browser');
    }

    // Initialize transcription service (singleton)
    transcriptionService.current = getVoiceTranscriptionService();

    // Cleanup on unmount
    return () => {
      if (transcriptionService.current?.isTranscribing()) {
        transcriptionService.current.stopTranscription();
      }
    };
  }, []);

  const handleTranscriptionResult = (result: TranscriptionResult) => {
    const newText = result.text.trim();
    if (!newText) return;
    // Apply different confidence thresholds for partial vs final results
    const confidenceThreshold = result.isPartial ? 0.7 : 0.4; // Partial: 0.7, Final: 0.4
    if (result.confidence && result.confidence <= confidenceThreshold) return;

    if (result.isPartial) {
      // Handle partial results for real-time feel
      if (!isProcessingPartial.current) {
        // First partial result - insert as partial
        currentPartialText.current = newText;
        isProcessingPartial.current = true;
        onTranscriptionText(newText, true, false);
      } else {
        // Update existing partial result
        if (newText !== currentPartialText.current) {
          currentPartialText.current = newText;
          onTranscriptionText(newText, true, true);
        }
      }
    } else {
      // Handle final results
      // Check if this is a duplicate of already processed text
      if (!processedTexts.current.has(newText)) {
        processedTexts.current.add(newText);

        if (isProcessingPartial.current) {
          // Replace partial with final - send the full final text
          onTranscriptionText(newText, false, true);
          isProcessingPartial.current = false;
          currentPartialText.current = '';
        } else {
          // Insert final text directly
          onTranscriptionText(newText, false, false);
        }
      } else if (isProcessingPartial.current) {
        // Finalize existing partial text with the current partial content
        isProcessingPartial.current = false;
        const textToFinalize = currentPartialText.current;
        currentPartialText.current = '';
        onTranscriptionText(textToFinalize, false, true);
      }
    }
  };

  const handleTranscriptionError = (error: Error) => {
    console.error('Transcription error:', error);
    setState('error');

    let errorMessage = 'Transcription failed';
    if (error.message.includes('Microphone access denied')) {
      errorMessage = 'Please allow microphone access to use voice transcription';
    } else if (error.message.includes('AWS')) {
      errorMessage = 'AWS Transcribe service error. Please check configuration.';
    } else if (error.message.includes('network') || error.message.includes('Network')) {
      errorMessage = 'Network error. Please check your internet connection.';
    }

    message.error(errorMessage);

    // Reset to idle state after error
    setTimeout(() => {
      setState('idle');
    }, 2000);
  };

  const handleTranscriptionEnd = () => {
    setState('idle');
    if (processedTexts.current.size > 0) {
      message.success('Voice transcription completed');
    }
  };

  const startTranscription = async () => {
    if (!isSupported || !transcriptionService.current || state !== 'idle') {
      return;
    }

    try {
      setState('connecting');
      // Reset deduplication tracking for new session
      processedTexts.current.clear();
      lastInsertedText.current = '';
      currentPartialText.current = '';
      isProcessingPartial.current = false;

      const options: TranscriptionOptions = {
        onTranscription: handleTranscriptionResult,
        onError: handleTranscriptionError,
        onEnd: handleTranscriptionEnd,
        onStreamReady: () => {
          // Called when AWS stream is ready to receive audio
          setState('recording');
        },
      };

      await transcriptionService.current.startTranscription(options);
    } catch (error) {
      handleTranscriptionError(error as Error);
    }
  };

  const stopTranscription = async () => {
    if (!transcriptionService.current || state === 'idle') {
      return;
    }

    try {
      setState('processing');
      await transcriptionService.current.stopTranscription();
    } catch (error) {
      console.error('Error stopping transcription:', error);
      setState('idle');
    }
  };

  const getButtonIcon = () => {
    switch (state) {
      case 'connecting':
        return <LoadingOutlined spin />;
      case 'recording':
        return <div className="recording-indicator">🔴</div>;
      case 'processing':
        return <LoadingOutlined spin />;
      case 'error':
        return <span style={{ color: '#ff4d4f' }}>⚠️</span>;
      default:
        return (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 9C8.10457 9 9 8.10457 9 7V3C9 1.89543 8.10457 1 7 1C5.89543 1 5 1.89543 5 3V7C5 8.10457 5.89543 9 7 9Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M11 6V7C11 9.20914 9.20914 11 7 11C4.79086 11 3 9.20914 3 7V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7 11V13M7 13H5M7 13H9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
    }
  };

  const getButtonText = () => {
    switch (state) {
      case 'connecting':
        return 'Connecting...';
      case 'recording':
        return 'Stop Recording';
      case 'processing':
        return 'Processing...';
      case 'error':
        return 'Error';
      default:
        return 'Voice Transcribe';
    }
  };

  const getButtonType = () => {
    switch (state) {
      case 'connecting':
        return 'default' as const;
      case 'recording':
        return 'primary' as const;
      case 'processing':
        return 'default' as const;
      case 'error':
        return 'default' as const;
      default:
        return 'default' as const;
    }
  };

  const getTooltipText = () => {
    if (!isSupported) {
      return 'Voice transcription not supported in this browser';
    }
    switch (state) {
      case 'connecting':
        return 'Connecting to transcription service...';
      case 'recording':
        return 'Click to stop recording and transcribe';
      case 'processing':
        return 'Processing audio...';
      case 'error':
        return 'Transcription failed - click to retry';
      default:
        return 'Start voice transcription for medical dictation';
    }
  };

  const handleClick = () => {
    if (!isSupported || disabled) {
      return;
    }

    if (state === 'idle' || state === 'error') {
      startTranscription();
    } else if (state === 'recording') {
      stopTranscription();
    }
    // Note: connecting and processing states don't allow clicks
  };

  return (
    <Tooltip title={getTooltipText()}>
      <Button
        type={getButtonType()}
        size={size}
        icon={getButtonIcon()}
        onClick={handleClick}
        disabled={disabled || !isSupported || state === 'processing' || state === 'connecting'}
        danger={state === 'recording'}
        className={`transcribe-btn ${state} ${className}`}
        aria-label={getButtonText()}
        aria-disabled={disabled || !isSupported}
      >
        {getButtonText()}
      </Button>
    </Tooltip>
  );
};

export default VoiceTranscribeButton;